import { useState, useEffect, useMemo } from 'react'
import { usePlayerStore } from '../../stores'
import { apiClient } from '../../utils/api'
import { backendClient } from '../../utils/backend'
import {
  Users, AlertTriangle, ArrowUpRight, Activity, Shield
} from 'lucide-react'
import type { AdminPage } from './AdminLayout'

interface AdminOverviewProps {
  onNavigate: (page: AdminPage) => void
}

type ChartRange = 'daily' | 'weekly' | 'monthly' | 'annual'

const rangeOptions: { id: ChartRange; label: string }[] = [
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'annual', label: 'Annual' },
]

export default function AdminOverview({ onNavigate }: AdminOverviewProps) {
  const { players, clubs } = usePlayerStore()
  const [profiles, setProfiles] = useState<any[]>([])
  const [claims, setClaims] = useState<any[]>([])
  const [chartRange, setChartRange] = useState<ChartRange>('monthly')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      try {
        const [p, c] = await Promise.all([
          apiClient.getProfiles().catch(() => []),
          backendClient.from('claim_requests').select('*').then(r => r.data || [], () => []),
        ])
        setProfiles(Array.isArray(p) ? p : [])
        setClaims(Array.isArray(c) ? c : [])
      } catch { } finally { setLoading(false) }
    }
    load()
  }, [])

  const stats = useMemo(() => {
    const totalUsers = profiles.length
    const totalPlayers = players.length
    const totalClubs = clubs.length
    const totalScouts = profiles.filter((p: any) => p.role === 'scout').length
    const verified = profiles.filter((p: any) => p.is_verified).length
    const pendingClaims = claims.filter((c: any) => c.status === 'pending').length
    return { totalUsers, totalPlayers, totalClubs, totalScouts, verified, pendingClaims }
  }, [profiles, clubs, claims])

  const rangeData = useMemo(() => {
    const now = new Date()
    const byDay = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(now)
      date.setDate(now.getDate() - (6 - index))
      const label = date.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })
      const value = profiles.filter((profile: any) => {
        const created = profile.created_at ? new Date(profile.created_at) : null
        return created && created.toDateString() === date.toDateString()
      }).length
      return { label, value }
    })

    const byWeek = Array.from({ length: 6 }, (_, index) => {
      const start = new Date(now)
      start.setDate(now.getDate() - (5 - index) * 7)
      const end = new Date(start)
      end.setDate(start.getDate() + 6)
      const label = `${start.toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}`
      const value = profiles.filter((profile: any) => {
        const created = profile.created_at ? new Date(profile.created_at) : null
        if (!created) return false
        return created >= start && created <= end
      }).length
      return { label, value }
    })

    const byMonth = Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1)
      const label = date.toLocaleDateString('en-NG', { month: 'short' })
      const value = profiles.filter((profile: any) => {
        const created = profile.created_at ? new Date(profile.created_at) : null
        if (!created) return false
        return created.getMonth() === date.getMonth() && created.getFullYear() === date.getFullYear()
      }).length
      return { label, value }
    })

    const byYear = Array.from({ length: 5 }, (_, index) => {
      const year = now.getFullYear() - (4 - index)
      const label = String(year)
      const value = profiles.filter((profile: any) => {
        const created = profile.created_at ? new Date(profile.created_at) : null
        return created && created.getFullYear() === year
      }).length
      return { label, value }
    })

    return {
      daily: byDay,
      weekly: byWeek,
      monthly: byMonth,
      annual: byYear,
    }
  }, [profiles])

  const chartSeries = rangeData[chartRange] || rangeData.monthly
  const chartPeak = Math.max(1, ...chartSeries.map((entry) => entry.value))

  const recentUsers = useMemo(() => {
    return [...profiles]
      .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 6)
      .map((p: any) => ({
        id: p.id,
        name: p.full_name || p.name || p.email?.split('@')[0] || 'Unknown',
        email: p.email,
        role: p.role || 'player',
        avatar: p.avatar_url,
        created_at: p.created_at,
        is_verified: p.is_verified,
      }))
  }, [profiles])

  const recentActivity = useMemo(() => {
    const items: { type: string; text: string; time: string; color: string }[] = []
    const recentPlayers = [...players]
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 3)
    recentPlayers.forEach(p => {
      items.push({ type: 'player', text: `New player profile: ${p.name}`, time: formatTimeAgo(p.created_at), color: 'text-[#7cff01]' })
    })
    const recentClaims = [...claims]
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
      .slice(0, 2)
    recentClaims.forEach(c => {
      items.push({ type: 'claim', text: `Profile claim ${c.status}`, time: formatTimeAgo(c.created_at), color: c.status === 'pending' ? 'text-yellow-400' : 'text-green-400' })
    })
    return items.slice(0, 5)
  }, [players, claims])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#7cff01] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats.totalUsers.toLocaleString(), icon: Users, color: 'text-[#7cff01]', bg: 'bg-[#7cff01]/10', change: '+12%', up: true },
          { label: 'Total Players', value: stats.totalPlayers.toLocaleString(), icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10', change: '+8%', up: true },
          { label: 'Clubs', value: stats.totalClubs.toLocaleString(), icon: Shield, color: 'text-purple-400', bg: 'bg-purple-500/10', change: '+5%', up: true },
          { label: 'Pending Claims', value: stats.pendingClaims.toString(), icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', change: stats.pendingClaims > 0 ? 'Requires attention' : 'All clear', up: false, urgent: stats.pendingClaims > 0 },
        ].map((stat, i) => (
          <div key={i} className="stagger-card animate-fade-in-up rounded-2xl border border-white/5 bg-[#161b22] p-5 hover:border-white/10 transition-all duration-300">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">{stat.label}</span>
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center`}>
                <stat.icon size={16} className={stat.color} />
              </div>
            </div>
            <p className="text-3xl font-black text-white">{stat.value}</p>
            <div className="flex items-center gap-1.5 mt-2">
              {stat.up ? (
                <ArrowUpRight size={12} className="text-[#7cff01]" />
              ) : stat.urgent ? (
                <AlertTriangle size={12} className="text-yellow-400" />
              ) : null}
              <span className={`text-[10px] font-black uppercase tracking-widest ${stat.urgent ? 'text-yellow-400' : stat.up ? 'text-[#7cff01]' : 'text-slate-500'}`}>
                {stat.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Chart + Activity Feed */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* User Growth Chart */}
        <div className="xl:col-span-2 rounded-2xl border border-white/5 bg-[#161b22] p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h3 className="text-lg font-black text-white">User Growth</h3>
              <p className="text-xs text-slate-500 mt-0.5">Platform registration trends</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {rangeOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setChartRange(option.id)}
                  className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.25em] transition ${
                    chartRange === option.id
                      ? 'bg-[#7cff01]/15 border border-[#7cff01]/25 text-[#7cff01]'
                      : 'border border-white/10 bg-white/5 text-slate-400 hover:text-white'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: 'Players', value: players.length, tone: 'text-[#7cff01]' },
              { label: 'Scouts', value: profiles.filter((profile: any) => profile.role === 'scout').length, tone: 'text-blue-400' },
              { label: 'Clubs', value: profiles.filter((profile: any) => profile.role === 'club').length, tone: 'text-purple-400' },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl border border-white/5 bg-[#0d1218] p-3">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">{item.label}</p>
                <p className={`mt-2 text-2xl font-black ${item.tone}`}>{item.value}</p>
              </div>
            ))}
          </div>

          <div className="flex items-end gap-3 h-52">
            {chartSeries.map((item, index) => (
              <div key={`${item.label}-${index}`} className="flex-1 flex flex-col items-center gap-2">
                <div className="flex h-full w-full items-end justify-center">
                  <div
                    className="w-full rounded-t-xl bg-gradient-to-t from-[#7cff01]/40 to-[#7cff01] shadow-[0_0_18px_rgba(124,255,1,0.2)] transition-all duration-500"
                    style={{ height: `${Math.max(12, (item.value / chartPeak) * 100)}%`, animationDelay: `${index * 80}ms` }}
                  />
                </div>
                <span className="text-[9px] font-bold text-slate-600 uppercase text-center leading-tight">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Feed */}
        <div className="rounded-2xl border border-white/5 bg-[#161b22] p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-black text-white uppercase tracking-widest">Recent Activity</h3>
            <Activity size={14} className="text-slate-500" />
          </div>
          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">No recent activity</p>
            ) : recentActivity.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${item.type === 'player' ? 'bg-[#7cff01]' : 'bg-yellow-400'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{item.text}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{item.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* User Directory Preview */}
      <div className="rounded-2xl border border-white/5 bg-[#161b22] p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-black text-white uppercase tracking-widest">User Directory</h3>
          <button
            onClick={() => onNavigate('users')}
            className="text-[10px] font-black uppercase tracking-widest text-[#7cff01] hover:text-[#8eff1a] transition flex items-center gap-1"
          >
            View All <ArrowUpRight size={12} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="pb-3 text-[10px] font-black uppercase tracking-widest text-slate-500">User</th>
                <th className="pb-3 text-[10px] font-black uppercase tracking-widest text-slate-500">Role</th>
                <th className="pb-3 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                <th className="pb-3 text-[10px] font-black uppercase tracking-widest text-slate-500 hidden sm:table-cell">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {recentUsers.map(u => (
                <tr key={u.id} className="hover:bg-white/[0.02] transition">
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {u.avatar ? (
                          <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs font-bold text-slate-400">{u.name[0]}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">{u.name}</p>
                        <p className="text-[10px] text-slate-500">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                      u.role === 'admin' ? 'bg-[#7cff01]/10 text-[#7cff01]' :
                      u.role === 'scout' ? 'bg-blue-500/10 text-blue-400' :
                      u.role === 'club' ? 'bg-purple-500/10 text-purple-400' :
                      'bg-white/5 text-slate-400'
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${u.is_verified ? 'bg-[#7cff01]' : 'bg-slate-500'}`} />
                      <span className={`text-xs font-bold ${u.is_verified ? 'text-[#7cff01]' : 'text-slate-500'}`}>
                        {u.is_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 hidden sm:table-cell">
                    <span className="text-xs text-slate-500">{formatDate(u.created_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function formatTimeAgo(date: string | undefined): string {
  if (!date) return 'Unknown'
  const diff = Date.now() - new Date(date).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function formatDate(date: string | undefined): string {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' })
}
